<template>
  <div class="contacts-container">
    <TableFiltersContainer padding="2px 0px 12px 0px">
      <AwTextField
        v-model="contactsState.selectedFilters.search"
        leading-icon="i-ph-magnifying-glass"
        placeholder="Rechercher un contact"
        class="min-w-fit"
        :debounce-delay="200"
      />
      <TableStatusSelect
        v-model:status="contactsState.selectedFilters.status"
        :status-options="[
          { label: 'Lead', value: 'lead' },
          { label: 'Prospect', value: 'prospect' },
          { label: 'Client', value: 'client' },
        ]"
      />
      <TableFiltersResetButton @click="resetFilters" />
      <AwButton color="secondary" variant="outline" class="ml-auto" @click="showCreateModal = true">
        <AwIcon name="i-ph-plus" />
        Ajouter un contact
      </AwButton>
    </TableFiltersContainer>
    <TableInteractive
      :key="tableKey"
      v-model:sorting="contactsState.sorting"
      :loading="contactsState.loading"
      :enable-sorting="true"
      :manual-sorting="true"
      :on-select="
        (row: any) => {
          selectedContactId = row.original.id;
          showContactSheet = true;
        }
      "
      :data="contacts"
      :columns="tableColumns"
      :empty-state-type="emptyStateType"
      :empty-state-reset-filters="resetFilters"
      height="100%"
    >
      <template #name-cell="{ row }">
        <TableCellContainer :is-loading="contactsState.loading" :skeletons="['text']">
          <TextEllipsis>
            {{ getFullName(row.original) }}
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #status-cell="{ row }">
        <TableCellContainer :is-loading="contactsState.loading" :skeletons="['text']">
          <div class="w-14 flex">
            <ContactStatusBadge :status="row.original.status" />
          </div>
        </TableCellContainer>
      </template>
      <template #email-cell="{ row }">
        <TableCellContainer :is-loading="contactsState.loading" :skeletons="['text']">
          <div style="display: flex; align-items: center; gap: 4px">
            <TextEllipsis>
              {{ row.original.email || "-" }}
            </TextEllipsis>
            <Copy v-if="row.original.email" :text="row.original.email" />
          </div>
        </TableCellContainer>
      </template>
      <template #phone-cell="{ row }">
        <TableCellContainer :is-loading="contactsState.loading" :skeletons="['text']">
          <div style="color: var(--gray-2)">
            <TextEllipsis>
              {{ row.original.phone_number ? stylifyPhoneNumber(row.original.phone_number) : "-" }}
            </TextEllipsis>
          </div>
        </TableCellContainer>
      </template>
      <template #job-cell="{ row }">
        <TableCellContainer :is-loading="contactsState.loading" :skeletons="['text']">
          <TextEllipsis>
            {{ row.original.job_title || "-" }}
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #agencies-cell="{ row }">
        <TableCellContainer :is-loading="contactsState.loading" :skeletons="['text']">
          <div v-if="row.original.agencies?.at(0)?.agency?.name" style="display: flex; align-items: center; gap: 4px">
            <AwTooltip :text="getAgenciesNames(row.original)">
              <TextEllipsis>
                {{ row.original.agencies?.at(0)?.agency?.name }}
              </TextEllipsis>
            </AwTooltip>
            <div v-if="row.original.agencies?.length > 1">+{{ row.original.agencies?.length - 1 }}</div>
          </div>
          <div v-else>-</div>
        </TableCellContainer>
      </template>
      <template #actions="{ row }">
        <TableRowActions
          v-if="isRemoveContactButtonEnabled"
          :row="row"
          :actions="[
            {
              label: 'Supprimer le contact',
              color: 'critical',
              icon: 'i-ph-minus-circle',
              onClick: () => {
                showArchiveContactModal = true;
                selectedContact = row.original;
              },
            },
          ]"
        />
      </template>
      <template #footer>
        <TablePagination
          :total-items="contactsState.totalItems"
          :page-size="contactsState.selectedFilters.pageSize"
          :page-index="contactsState.selectedFilters.pageIndex"
          @update:page-size="contactsState.selectedFilters.pageSize = $event"
          @update:page-index="contactsState.selectedFilters.pageIndex = $event"
        />
      </template>
    </TableInteractive>
    <LazyCompanyRemoveContact
      v-if="showArchiveContactModal && selectedContact && isRemoveContactButtonEnabled"
      :contact="selectedContact"
      :is-open="showArchiveContactModal"
      @update:open="showArchiveContactModal = false"
      @on-update="query()"
    />
    <LazyContactFormModal
      v-if="showCreateModal"
      :company-id="companyId"
      @on-save="
        showCreateModal = false;
        query();
      "
      @on-close="showCreateModal = false"
    />
  </div>
  <div v-if="selectedContactId">
    <LazyContactSheet
      v-if="showContactSheet"
      :contact_id="selectedContactId"
      :is-removable="isRemoveContactButtonEnabled"
      @on-close="showContactSheet = false"
      @on-remove="query"
    />
  </div>
</template>

<script setup lang="ts">
import { useToast } from "~/composables/useToast";
import { useContacts } from "~/components/contact/composables/useContacts";
import type { TableEmptyStateType } from "~/utils/types";
import { getFullName } from "@asap/shared/utils/people";
import { useTableColumn } from "~/composables/useTableColumn";
import type { Contact } from "@asap/shared/schemas/contact";
import { stylifyPhoneNumber } from "@asap/shared/utils/phoneNumber";
import { useFeatureFlag } from "~/composables/useFeatureFlag";

const props = defineProps<{
  companyId: string;
}>();

const showContactSheet = ref(false);
const showArchiveContactModal = ref(false);

const showCreateModal = ref(false);
const selectedContact = ref<Contact | null>(null);
const selectedContactId = ref<string | null>(null);

const contacts = ref<Contact[]>([]);
const emptyStateType = ref<TableEmptyStateType>("no-data");

const { addToastError } = useToast();
const { isFeatureFlagEnabled } = useFeatureFlag();
const isRemoveContactButtonEnabled = isFeatureFlagEnabled("remove-contact-button");
const { contactsState, selectMultipleForTable, convertSortingToOrders } = useContacts();

const getAgenciesNames = (contact: Contact) => {
  return contact.agencies
    ?.map((agency) => agency?.agency)
    .filter(Boolean)
    .map((agency) => {
      if (agency.address?.postal_code) {
        return `${agency.name} (${agency.address.postal_code})`;
      }
      return agency.name;
    })
    .join(" / ");
};

const { tableKey, tableColumns } = useTableColumn(
  [
    {
      label: "Statut",
      value: {
        accessorKey: "status",
        header: "Statut",
        maxSize: 60,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Nom",
      value: {
        accessorKey: "name",
        header: "Nom",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Poste",
      value: {
        accessorKey: "job",
        header: "Poste",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Email",
      value: {
        accessorKey: "email",
        header: "Email",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Téléphone",
      value: {
        accessorKey: "phone",
        header: "Téléphone",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Agences",
      value: {
        accessorKey: "agencies",
        header: "Agences",
      },
      blocked: true,
      selected: true,
    },
  ],
  "contacts-table-columns"
);

const resetFilters = () => {
  contactsState.selectedFilters.search = undefined;
  contactsState.selectedFilters.status = undefined;
  contactsState.selectedFilters.businessUnitIds = undefined;
  contactsState.sorting = [{ id: "created_at", desc: true }];
};

const query = async function () {
  try {
    const orders = convertSortingToOrders(contactsState.sorting);
    const { data, count } = await selectMultipleForTable({
      ...contactsState.selectedFilters,
      companyId: props.companyId,
      orders,
    });
    if (data) contacts.value = data;
    if (count !== null) contactsState.totalItems = count;
    if (contactsState.selectedFilters.search) {
      emptyStateType.value = "no-data-with-filters";
    } else {
      emptyStateType.value = "no-data";
    }
  } catch (error) {
    emptyStateType.value = "error";
    addToastError(
      {
        title: "Une erreur est survenue lors de la récupération des contacts",
      },
      error
    );
  }
};

const isInitialized = ref(false);

onMounted(async () => {
  contactsState.selectedFilters.pageIndex = 0;
  await query();
  isInitialized.value = true;
});

watch(() => contactsState.selectedFilters.pageIndex, query);

watch(
  () => [
    contactsState.sorting,
    contactsState.selectedFilters.search,
    contactsState.selectedFilters.pageSize,
    contactsState.selectedFilters.companyId,
    contactsState.selectedFilters.businessUnitIds,
    contactsState.selectedFilters.status,
  ],
  async () => {
    if (!isInitialized.value) return;
    contactsState.selectedFilters.pageIndex = 0;
    await query();
  }
);
</script>

<style scoped lang="scss">
.contacts-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.toggle_wrapper {
  margin: 0 auto;
  margin-top: 1rem;
}
</style>
